import {useCallback, useEffect, useMemo, useState} from 'react';
import {EventHandler} from 'powerbi-client-react';
import {models, Report as EmbeddedObject} from 'powerbi-client';
import {
  EmbeddingDetails,
  EmbedToken,
  Report
} from '../components/reports/types';
import {getEmbeddingDetails, refreshReportsToken} from '../api/reports';
import {useSnackbarHook} from './useSnackbar';
import {MILLISECONDS_IN_MINUTE} from '../constants/constants';

type ReportHelperParams = {
  embedConfig: models.IReportEmbedConfiguration,
  getEmbeddingParams: (report: Report) => Promise<void>,
  isLoading: boolean,
  handleSetReport: (report) => void,
  eventHandlers: Map<string, EventHandler>
}

type ReportHelperProps = {
  initialConfig?: models.IReportEmbedConfiguration,
  report?: Report
}

const MINUTES_BEFORE_EXPIRATION = 10;
const INTERVAL_TIME_MS = 30000;

export const useReports = ({
  initialConfig,
  report
}: ReportHelperProps): ReportHelperParams => {
  const [isLoading, setIsLoading] = useState(true);
  const [embedConfig, setEmbedConfig] =
    useState<models.IReportEmbedConfiguration>({
      ...initialConfig
    });
  const [embeddingDetails, setEmbeddingDetails] =
    useState<Nullable<EmbeddingDetails>>(null);
  const [embeddedReport, setEmbeddedReport] =
    useState<Nullable<EmbeddedObject>>(null);

  const {addErrorToast} = useSnackbarHook();

  const {powerbiReportId, powerbiGroupId} = report || {};
  const {embedToken, datasetId} = embeddingDetails || {};
  const {tokenExpiration} = embedToken || {};

  const handleSetReport = useCallback((newReport) => {
    setEmbeddedReport(newReport);
  }, []);

  const getEmbeddingParams = useCallback(async () => {
    setIsLoading(true);

    if (!powerbiReportId || !powerbiGroupId) return;

    try {
      const details =
        await getEmbeddingDetails(powerbiReportId, powerbiGroupId);

      const {embedUrl, embedToken} = details || {};

      setEmbedConfig((prevConfig) => ({
        ...prevConfig,
        embedUrl,
        accessToken: embedToken?.accessToken
      }));
      setEmbeddingDetails(details);
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [powerbiReportId, powerbiGroupId]);

  const updateConfig = useCallback((embedToken: EmbedToken) => {
    setEmbedConfig((prevConfig) => ({
      ...prevConfig,
      accessToken: embedToken.accessToken
    }));

    embeddedReport?.refresh();
  }, [embeddedReport]);

  const refreshToken = useCallback(async () => {
    try {
      const embedToken = await refreshReportsToken({
        reportId: powerbiReportId,
        datasetId,
        groupId: powerbiGroupId
      });

      updateConfig(embedToken);
    } catch (e) {
      addErrorToast(e?.message);
    }
  }, [powerbiReportId, powerbiGroupId, datasetId, updateConfig]);

  const checkToken = useCallback(async () => {
    if (!tokenExpiration) return;

    const currentTime = Date.now();
    const expiration = Date.parse(tokenExpiration);

    const timeUntilExpiration = expiration - currentTime;
    const timeToUpdate = MINUTES_BEFORE_EXPIRATION * MILLISECONDS_IN_MINUTE;

    if (timeUntilExpiration <= timeToUpdate) {
      refreshToken();
    }
  }, [refreshToken, tokenExpiration]);

  useEffect(() => {
    if (!report) return;

    const interval = setInterval(() => checkToken(), INTERVAL_TIME_MS);

    return () => clearInterval(interval);
  }, [checkToken]);

  const eventHandlers = useMemo(() => {
    return new Map([
      [
        'loaded',
        function() {}
      ],
      [
        'rendered',
        function() {}
      ],
      [
        'error',
        function() {}
      ]
    ]);
  }, []);

  useEffect(() => {
    getEmbeddingParams();
  }, [getEmbeddingParams]);

  return {
    embedConfig,
    getEmbeddingParams,
    isLoading,
    eventHandlers,
    handleSetReport
  };
};
